import type { SearchSource, SearchSuggestion } from '@akademi/site-builder-runtime';

/**
 * The header search box's backend, for a shop.
 *
 * <p>The runtime widget has two modes. Catalog mode posts to a catalog endpoint
 * and renders tabs for courses, blog posts, news, FAQs and employees — the
 * learning portal's vocabulary, which a shop has none of. Suggest mode asks the
 * host for hits and leaves the presentation alone, so that is the mode the
 * storefront's templates select (`shell.search.mode: 'suggest'`) and this is the
 * adapter behind it.
 *
 * <p>Suggestions come from the same listing endpoint the category pages use, so
 * a product that is not renderable — draft, out of channel, no slug — cannot
 * appear in the dropdown and then 404 when clicked. One source of truth about
 * what is on sale, rather than a second query that could disagree with it.
 */

/** The dropdown shows a handful; beyond that the answer is "press Enter". */
const DEFAULT_LIMIT = 6;

/** Newest first, so an empty query is still a sensible list. */
const RECENTS_KEY = 'sorsy.storefront.recents';
const MAX_RECENTS = 8;

interface ListingItem {
  slug?: string;
  publicPath?: string;
  title?: string;
  brandName?: string;
  heroImage?: { url?: string } | null;
  price?: { amount?: string; currency?: string } | null;
}

function readRecents(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    // Private browsing, a disabled store, or a value someone else wrote. Recents
    // are a convenience; none of that is worth breaking the search box over.
    return [];
  }
}

/**
 * Money for a one-line subtitle.
 *
 * <p>Deliberately not `Intl.NumberFormat` with a hardcoded locale: the amount
 * arrives already rounded by the backend's price resolver, and re-formatting it
 * here in a different locale to the page is how a price ends up displayed two
 * ways on one screen. The currency code is shown as-is for the same reason.
 */
function subtitle(item: ListingItem): string | undefined {
  const parts: string[] = [];
  if (item.brandName) parts.push(item.brandName);
  if (item.price?.amount) {
    const amount = Number(item.price.amount);
    const money = Number.isFinite(amount) ? amount.toFixed(2) : item.price.amount;
    parts.push(item.price.currency ? `${money} ${item.price.currency}` : `${money}`);
  }
  return parts.length ? parts.join(' · ') : undefined;
}

export function createStorefrontSearchSource({
  basePath = '',
}: {
  basePath?: string;
} = {}): SearchSource {
  return {
    async suggest({ query, limit, signal }) {
      const trimmed = query.trim();
      if (!trimmed) return [];

      const params = new URLSearchParams({
        q: trimmed,
        size: String(limit ?? DEFAULT_LIMIT),
        page: '0',
      });
      const response = await fetch(`${basePath}/api/storefront/listing?${params}`, {
        signal,
        headers: { accept: 'application/json' },
      });
      if (!response.ok) return [];

      const payload = (await response.json()) as { items?: ListingItem[] };
      const items = Array.isArray(payload.items) ? payload.items : [];

      return items.reduce<SearchSuggestion[]>((out, item) => {
        // publicPath is the canonical route the backend owns; the slug fallback
        // exists only for a payload that predates it. Without either there is
        // nowhere to send a click, so the row is dropped rather than rendered
        // as a dead one.
        const href = item.publicPath ?? (item.slug ? `/p/${item.slug}` : null);
        if (!href || !item.title) return out;
        out.push({
          id: item.slug ?? href,
          title: item.title,
          kind: 'product',
          href,
          thumbnail: item.heroImage?.url ?? undefined,
          description: subtitle(item),
        });
        return out;
      }, []);
    },

    // No trending feed: there is no query log yet, and inventing one would put
    // words in shoppers' mouths. An empty list renders as no trending section.
    async trending() {
      return [];
    },

    recents({ limit } = {}) {
      const all = readRecents();
      return limit ? all.slice(0, limit) : all;
    },

    pushRecent(query) {
      const trimmed = query.trim();
      if (!trimmed) return;
      try {
        const next = [trimmed, ...readRecents().filter((r) => r !== trimmed)].slice(0, MAX_RECENTS);
        window.localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
      } catch {
        /* see readRecents */
      }
    },

    clearRecents() {
      try {
        window.localStorage.removeItem(RECENTS_KEY);
      } catch {
        /* see readRecents */
      }
    },
  };
}
